import { ApolloClient, ApolloLink, createHttpLink, from, InMemoryCache, Operation } from '@apollo/client';
import { NetworkError } from '@apollo/client/errors';
import { onError } from '@apollo/client/link/error';
import { PlatformEnvironment } from '@mews/enums';

interface QueryErrorPayload {
    graphqlErrors: string[] | null;
    networkError: NetworkError | null;
    operationName: string;
    headers: Record<string, string>;
}

interface CreateApolloClientOptions {
    graphQLUrl: string;
    environment: PlatformEnvironment;
    getCustomHeaders: () => Record<string, string>;
    onQueryCalled: (
        data: Pick<Operation, 'variables' | 'operationName'> & { headers?: Record<string, string> }
    ) => void;
    onQueryError: (payload: QueryErrorPayload) => void;
}

export const createApolloClient = ({
    graphQLUrl,
    environment,
    getCustomHeaders: getAdditionalHeaders,
    onQueryCalled,
    onQueryError,
}: CreateApolloClientOptions) => {
    const httpHeaderLink = new ApolloLink((operation, forward) => {
        operation.setContext(({ headers = {} }) => ({
            headers: {
                ...headers,
                ...getAdditionalHeaders(),
            },
        }));

        onQueryCalled({
            operationName: operation.operationName,
            variables: operation.variables,
            headers: operation.getContext()?.headers,
        });

        return forward(operation);
    });

    const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
        // this check without != null is intentional, it is typed as GraphQLErrors | undefined, but in real life empty string can be recieved
        const graphqlErrors = graphQLErrors ? graphQLErrors?.map(({ message }) => message) : null;

        onQueryError({
            networkError: networkError ?? null,
            graphqlErrors,
            operationName: operation.operationName,
            headers: operation.getContext()?.headers,
        });
    });

    const httpLink = createHttpLink({
        uri: graphQLUrl,
    });

    return new ApolloClient({
        uri: graphQLUrl,
        cache: new InMemoryCache(),
        link: from([errorLink, httpHeaderLink, httpLink]),
        connectToDevTools: environment === PlatformEnvironment.Development,
    });
};
